import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { LoginPage, VerifiedRoute, useAuth } from '@micronet/auth';
import { verifySession } from './lib/verifySession';

// The home route's code (the whole authenticated app: menus, tabs, panels) is
// only ever downloaded once VerifiedRoute below confirms the session — an
// unauthenticated client's initial load must never carry more than the login
// page, so this must stay a lazy import, not a static one.
const LazyApp = lazy(() => import('./App.tsx'));

export function FullPageLoading() {
  return <div className="flex h-screen items-center justify-center">Cargando…</div>;
}

// "/" — unauthenticated landing. Once MSAL reports a session, bounce to
// "/home"; the home route itself re-verifies with the backend before
// rendering anything, so this redirect is not a security boundary, only UX.
export function LoginRoute() {
  const { isAuthenticated, isLoading, error, signIn } = useAuth();
  if (isAuthenticated) return <Navigate to="/home" replace />;
  return (
    <LoginPage
      onSignIn={signIn}
      isSigningIn={isLoading}
      error={error}
      backgroundSrc="/andina-front.jpg"
      logoSrc="/logo_bmr.png"
    />
  );
}

// "/home" — the actual security boundary. A client with no MSAL session is
// bounced to "/"; one with a session still only gets the app once verifySession
// confirms the identity resolves to a real, active user in this app.
export function HomeRoute() {
  const { isAuthenticated, getAccessToken, signOut } = useAuth();
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return (
    <VerifiedRoute
      verify={() => verifySession(getAccessToken)}
      loading={<FullPageLoading />}
      onRejected={() => void signOut()}
    >
      <Suspense fallback={<FullPageLoading />}>
        <LazyApp />
      </Suspense>
    </VerifiedRoute>
  );
}
